import { FC, lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { CircularProgress } from '@mui/material';

import {
  PersonaSurveyPage,
  NameEntryPage,
  MarketingSurveyPage,
  GetStartedPage,
  ManualSleepInputPage,
  EnergyPredictionResultPage,
  EnergySituationPage,
  WelcomePage,
  AiPlanPreviewPage,
} from '../pages';
import {
  onboardingSteps,
  OnboardingProvider,
  CalendarProvider,
  CircadianProvider,
  TodoTasksProvider,
  WearablesProvider,
  AssistantChatProvider,
  // TodoTasksProvider kept — used by AiPlanPreviewPage
} from '../data-access';
import { PageTransition } from '../components';
import { ChunkErrorBoundary } from './ChunkErrorBoundary';

const LazyPowerUpPlanPage = lazy(
  () => import('../pages/Onboarding/PowerUpPlanPage/PowerUpPlanPage')
);

export const OnboardingRoutes: FC = () => {
  const location = useLocation();

  return (
    <OnboardingProvider onboardingSteps={onboardingSteps}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path={'welcome'}
            element={
              <PageTransition>
                <WelcomePage />
              </PageTransition>
            }
          />
          <Route
            path={'energy-situation'}
            element={
              <PageTransition>
                <EnergySituationPage />
              </PageTransition>
            }
          />
          <Route
            path={'get-started'}
            element={
              <PageTransition>
                <GetStartedPage />
              </PageTransition>
            }
          />
          <Route
            path={'name-entry'}
            element={
              <PageTransition>
                <NameEntryPage />
              </PageTransition>
            }
          />
          <Route
            path={'persona-survey'}
            element={
              <PageTransition>
                <PersonaSurveyPage />
              </PageTransition>
            }
          />
          <Route
            path={'marketing-survey'}
            element={
              <PageTransition>
                <MarketingSurveyPage />
              </PageTransition>
            }
          />
          <Route
            path={'manual-sleep-input'}
            element={
              <PageTransition>
                <ManualSleepInputPage />
              </PageTransition>
            }
          />
          <Route
            path={'energy-prediction-result'}
            element={
              <PageTransition>
                <EnergyPredictionResultPage />
              </PageTransition>
            }
          />
          <Route
            path={'power-up-plan'}
            element={
              <PageTransition>
                <CalendarProvider>
                  <WearablesProvider>
                    <ChunkErrorBoundary>
                      <Suspense
                        fallback={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: 48,
                            }}
                          >
                            <CircularProgress />
                          </div>
                        }
                      >
                        <LazyPowerUpPlanPage />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </WearablesProvider>
                </CalendarProvider>
              </PageTransition>
            }
          />
          <Route
            path={'ai-plan-preview'}
            element={
              <PageTransition>
                <CalendarProvider>
                  <TodoTasksProvider>
                    <CircadianProvider>
                      <AssistantChatProvider>
                        <AiPlanPreviewPage />
                      </AssistantChatProvider>
                    </CircadianProvider>
                  </TodoTasksProvider>
                </CalendarProvider>
              </PageTransition>
            }
          />
        </Routes>
      </AnimatePresence>
    </OnboardingProvider>
  );
};
